import { COMMON_LABELS, BASIS_LABELS, type CommonKey } from "./compositeReportModel.ts";
import { formatCell } from "./customReportModel.ts";
import type { CustomReportFieldDto } from "./api.ts";
import {
  listReportTemplates,
  getReportTemplate,
  runReportTemplate,
  copyReportTemplate,
  type ReportTemplateDetailDto,
  type CustomReportResultDto,
} from "./api.ts";

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const item = document.createElement(tag);
  if (text !== undefined) item.textContent = text;
  return item;
}
function button(text: string, action: () => void, primary = false): HTMLButtonElement {
  const item = node("button", text);
  item.type = "button";
  item.className = primary ? "cr-btn-primary" : "cr-btn";
  item.addEventListener("click", action);
  return item;
}
function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
function values(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : "";
}
interface CompositeSummaryLabels {
  datasetLabel?: (datasetId: string) => string;
  fieldLabel: (field: string, datasetId: string) => string;
  field?: (field: string, datasetId: string) => CustomReportFieldDto | undefined;
  dateBasis?: (datasetId: string) => string | undefined;
}
function compositeSpecSummary(
  spec: Record<string, unknown>,
  ownScope: string,
  labels: CompositeSummaryLabels,
): HTMLElement {
  const summary = node("dl");
  summary.className = "cr-template-spec";
  const appendRow = (name: string, display: string) =>
    summary.append(node("dt", name), node("dd", display));
  const sources = values(spec["sources"]).map(record);
  const sourceName = (sourceItem: Record<string, unknown>) => {
    const datasetId = text(sourceItem["dataset"]);
    const found = labels.datasetLabel?.(datasetId);
    return found && found !== datasetId ? found : "Unknown source";
  };
  const fieldName = (sourceItem: Record<string, unknown>, fieldId: unknown) => {
    const id = text(fieldId),
      found = labels.fieldLabel(id, text(sourceItem["dataset"]));
    return found && found !== id ? found : "Unknown field";
  };
  const aggregateWords: Record<string, string> = {
    sum: "Total",
    count: "Count",
    min: "Minimum",
    max: "Maximum",
    avg: "Average",
    ratio: "Ratio",
    share: "Share",
  };
  const filterWords: Record<string, string> = {
    eq: "is",
    neq: "is not",
    in: "is one of",
    not_in: "is not one of",
    contains: "contains",
    gt: "is greater than",
    gte: "is at least",
    lt: "is less than",
    lte: "is at most",
    between: "is between",
    is: "is",
  };
  const measureName = (sourceItem: Record<string, unknown>, measureItem: Record<string, unknown>) =>
    `${sourceName(sourceItem)} · ${fieldName(sourceItem, measureItem["field"])} (${aggregateWords[text(measureItem["agg"])] ?? "Unknown aggregation"})`;
  const referenceName = (reference: unknown) => {
    for (const sourceItem of sources) {
      const measureItem = values(sourceItem["measures"])
        .map(record)
        .find((candidate) => `${text(sourceItem["id"])}_${text(candidate["as"])}` === reference);
      if (measureItem) return measureName(sourceItem, measureItem);
    }
    const calculation = values(spec["calculations"])
      .map(record)
      .find((candidate) => candidate["as"] === reference);
    return calculation
      ? text(calculation["label"])
      : (COMMON_LABELS[text(reference) as CommonKey] ?? "Unknown result");
  };
  let expressionNodes = 0;
  const expressionName = (expression: unknown, depth = 1): string => {
    if (++expressionNodes > 32 || depth > 8) return "Unknown calculation";
    const item = record(expression);
    if (item["op"] === "constant") return text(item["value"]);
    if (item["op"] === "measure") {
      const sourceItem = sources.find((candidate) => candidate["id"] === item["source"]);
      const measureItem =
        sourceItem &&
        values(sourceItem["measures"])
          .map(record)
          .find((candidate) => candidate["as"] === item["measure"]);
      return sourceItem && measureItem
        ? `“${measureName(sourceItem, measureItem)}”`
        : "Unknown source measure";
    }
    const operator = (
      { add: "+", subtract: "−", multiply: "×", divide: "÷" } as Record<string, string>
    )[text(item["op"])];
    return operator
      ? `(${expressionName(item["left"], depth + 1)} ${operator} ${expressionName(item["right"], depth + 1)})`
      : "Unknown calculation";
  };
  appendRow("Sources", sources.map(sourceName).join(", "));
  const range = record(spec["dateRange"]);
  appendRow(
    "Date range",
    range["kind"] === "absolute"
      ? `${text(range["from"])} to ${text(range["to"])}`
      : text(range["preset"]).replaceAll("_", " "),
  );
  appendRow("Locations", ownScope);
  appendRow(
    "Group by",
    values(spec["dimensions"])
      .map((group) => COMMON_LABELS[text(group) as CommonKey] ?? "Unknown group")
      .join(", ") || "None",
  );
  for (const sourceItem of sources) {
    const name = sourceName(sourceItem),
      datasetId = text(sourceItem["dataset"]);
    appendRow(
      `${name}: date basis`,
      BASIS_LABELS[labels.dateBasis?.(datasetId) ?? ""] ?? "-",
    );
    appendRow(
      `${name}: measures`,
      values(sourceItem["measures"])
        .map(record)
        .map((measureItem) => measureName(sourceItem, measureItem))
        .join("; "),
    );
    const filters = values(sourceItem["filters"]).map(record);
    appendRow(
      `${name}: filters`,
      filters.length
        ? filters
            .map((filterItem) => {
              const field = labels.field?.(text(filterItem["field"]), datasetId);
              const selected =
                filterItem["values"] === undefined
                  ? [filterItem["value"]]
                  : values(filterItem["values"]);
              const filterValue = (chosen: unknown) =>
                field === undefined
                  ? text(chosen)
                  : field.type === "percent"
                    ? `${text(chosen)}%`
                    : formatCell(
                        typeof chosen === "string" ||
                          typeof chosen === "number" ||
                          typeof chosen === "boolean"
                          ? chosen
                          : null,
                        field.type === "enum" ? "text" : field.type,
                        field.valueLabels,
                      );
              return `${fieldName(sourceItem, filterItem["field"])}${filterItem["agg"] === undefined ? "" : ` (${aggregateWords[text(filterItem["agg"])] ?? "Unknown aggregation"})`} ${filterWords[text(filterItem["op"])] ?? "Unknown comparison"} ${selected.map(filterValue).join(", ")}`;
            })
            .join("; ")
        : "None",
    );
  }
  const calculations = values(spec["calculations"]).map(record);
  if (!calculations.length) appendRow("Calculations", "None");
  for (const calculation of calculations)
    appendRow(
      text(calculation["label"]),
      `${expressionName(calculation["expression"])}${calculation["percent"] === true ? " (show as %)" : ""}`,
    );
  const order = values(spec["sort"]).map(record);
  if (order.length)
    appendRow(
      "Sort",
      order
        .map(
          (sortItem) =>
            `${referenceName(sortItem["by"])} ${sortItem["dir"] === "asc" ? "ascending" : "descending"}`,
        )
        .join("; "),
    );
  const output = record(spec["output"]);
  appendRow("Rows", spec["limit"] == null ? "Maximum permitted" : text(spec["limit"]));
  appendRow("Totals", output["totals"] === true ? "On" : "Off");
  if (spec["compare"] !== undefined)
    appendRow("Compare", text(record(spec["compare"])["kind"]).replaceAll("_", " "));
  const chart = record(output["chart"]);
  if (chart["type"] !== undefined) {
    appendRow("Chart", text(chart["type"]).replaceAll("_", " "));
    appendRow(
      "Chart fields",
      [chart["x"], ...values(chart["y"]), chart["series"]]
        .filter((key) => key != null)
        .map(referenceName)
        .join(", "),
    );
  }
  const tile = record(output["tile"]);
  if (tile["measure"] !== undefined)
    appendRow("Dashboard tile", `${text(tile["label"])}: ${referenceName(tile["measure"])}`);
  return summary;
}
function specSummary(
  spec: Record<string, unknown>,
  label: (field: string) => string,
  ownScope: string,
  compositeLabels?: CompositeSummaryLabels,
): HTMLElement {
  if (spec["specVersion"] === 2)
    return compositeSpecSummary(
      spec,
      ownScope,
      compositeLabels ?? { fieldLabel: () => "Unknown field" },
    );
  const result = node("dl");
  result.className = "cr-template-spec";
  const row = (name: string, value: string) => result.append(node("dt", name), node("dd", value));
  const aggregationNames: Record<string, string> = {
    sum: "Total",
    count: "Count",
    min: "Minimum",
    max: "Maximum",
    avg: "Average",
    ratio: "Ratio",
    share: "Share",
  };
  const operatorNames: Record<string, string> = {
    eq: "is",
    neq: "is not",
    in: "is one of",
    not_in: "is not one of",
    contains: "contains",
    gt: "is greater than",
    gte: "is at least",
    lt: "is less than",
    lte: "is at most",
    between: "is between",
    is: "is",
  };
  const measures = values(spec["measures"]).map(record);
  const referenceLabel = (value: unknown) => {
    const measure = measures.find((item) => item["as"] === value);
    return measure === undefined
      ? label(text(value))
      : `${label(text(measure["field"]))} (${aggregationNames[text(measure["agg"])] ?? text(measure["agg"])})`;
  };
  const range = record(spec["dateRange"]);
  row(
    "Date range",
    range["kind"] === "absolute"
      ? `${text(range["from"])} to ${text(range["to"])}`
      : text(range["preset"]).replaceAll("_", " "),
  );
  row("Locations", ownScope);
  row(
    "Group by",
    values(spec["dimensions"])
      .map((field) => label(text(field)))
      .join(", ") || "None",
  );
  row(
    "Measures",
    values(spec["measures"])
      .map((measure) => {
        const item = record(measure);
        return `${label(text(item["field"]))} (${aggregationNames[text(item["agg"])] ?? text(item["agg"])})`;
      })
      .join(", "),
  );
  const filters = values(spec["filters"]);
  row(
    "Filters",
    filters.length === 0
      ? "None"
      : filters
          .map((filter) => {
            const item = record(filter);
            const selected =
              item["values"] === undefined ? [item["value"]] : values(item["values"]);
            // The server normalizes every measure filter's default aggregation before review/publication.
            const aggregate = text(item["agg"]);
            const field = `${label(text(item["field"]))}${aggregate === "" ? "" : ` (${aggregationNames[aggregate] ?? aggregate})`}`;
            return `${field} ${operatorNames[text(item["op"])] ?? text(item["op"])} ${selected.map(text).join(", ")}`;
          })
          .join("; "),
  );
  const sort = values(spec["sort"]);
  if (sort.length > 0)
    row(
      "Sort",
      sort
        .map((entry) => {
          const item = record(entry);
          return `${referenceLabel(item["by"])} ${item["dir"] === "asc" ? "ascending" : "descending"}`;
        })
        .join(", "),
    );
  const output = record(spec["output"]);
  row("Rows", spec["limit"] == null ? "Maximum permitted" : text(spec["limit"]));
  row("Totals", output["totals"] === true ? "On" : "Off");
  if (spec["compare"] !== undefined)
    row("Compare", text(record(spec["compare"])["kind"]).replaceAll("_", " "));
  const chart = record(output["chart"]);
  if (chart["type"] !== undefined) {
    row("Chart", text(chart["type"]).replaceAll("_", " "));
    row(
      "Chart fields",
      [chart["x"], ...values(chart["y"]), chart["series"]]
        .filter((field) => field != null)
        .map(referenceLabel)
        .join(", "),
    );
  }
  return result;
}

export function mountRecipientTemplates(
  parent: HTMLElement,
  deps: {
    baseUrl: string;
    tenantId: string;
    canCopy: boolean;
    isCurrent: () => boolean;
    fieldLabel: (field: string, datasetId: string) => string;
    datasetLabel?: (datasetId: string) => string;
    field?: (field: string, datasetId: string) => CustomReportFieldDto | undefined;
    dateBasis?: (datasetId: string) => string | undefined;
    renderResult: (result: CustomReportResultDto) => HTMLElement;
    openCopy: (id: string) => void;
    /** RPTMINE1: a template id to open and run on arrival, instead of the list (the Reports home chose it). */
    autoOpen?: string;
  },
): void {
  const panel = node("section");
  panel.className = "cr-card cr-template-library";
  panel.dataset["testid"] = "cr-template-library";
  panel.append(node("h3", "From your franchisor"));
  parent.append(panel);
  let operation = 0;
  const current = () => panel.isConnected && deps.isCurrent();
  const message = node("p");
  message.setAttribute("role", "status");
  panel.append(message);
  const content = node("div");
  panel.append(content);
  async function list() {
    const token = ++operation;
    message.textContent = "Loading.";
    const result = await listReportTemplates(deps.baseUrl, deps.tenantId);
    if (!current() || token !== operation) return;
    if (result.status !== 200 || !Array.isArray(result.body.items)) {
      message.textContent = result.body?.message ?? "Could not open templates.";
      return;
    }
    message.textContent = result.body.items.length === 0 ? "No templates" : "";
    content.replaceChildren();
    for (const item of result.body.items) {
      const row = node("div");
      row.className = "cr-template-row";
      const open = button(item.title, () => void detail(item.id));
      open.dataset["testid"] = "cr-template-open";
      row.append(open, node("span", `${item.datasetLabel}, version ${item.versionNumber}`));
      content.append(row);
    }
  }
  async function detail(id: string, runAtOnce = false) {
    const token = ++operation;
    message.textContent = "Loading.";
    const result = await getReportTemplate(deps.baseUrl, deps.tenantId, id);
    if (!current() || token !== operation) return;
    if (result.status !== 200 || typeof result.body.id !== "string") {
      message.textContent = result.body?.message ?? "This template is unavailable.";
      return;
    }
    const template: ReportTemplateDetailDto = result.body;
    message.textContent = "";
    content.replaceChildren();
    const resultHost = node("div");
    const status = node("p", "Not run");
    status.setAttribute("role", "status");
    const controls = node("div");
    controls.className = "cr-actions";
    const copyKey = crypto.randomUUID();
    const run = button("Run", () => void runNow(), true);
    run.dataset["testid"] = "cr-template-run";
    const copy = button("Save a copy", () => void copyNow());
    copy.disabled = !deps.canCopy;
    copy.dataset["testid"] = "cr-template-copy";
    controls.append(
      run,
      copy,
      button("Back", () => void list()),
    );
    content.append(
      node("h3", template.title),
      node("p", `${template.datasetLabel}, version ${template.versionNumber}`),
      specSummary(
        template.spec,
        (field) => deps.fieldLabel(field, template.dataset),
        "My permitted locations",
        deps,
      ),
      controls,
      status,
      resultHost,
    );
    if (runAtOnce) void runNow();
    async function runNow() {
      const request = ++operation;
      run.disabled = true;
      copy.disabled = true;
      status.textContent = "Running.";
      resultHost.replaceChildren();
      const got = await runReportTemplate(
        deps.baseUrl,
        deps.tenantId,
        template.id,
        template.publicationVersionId,
      );
      if (!current() || request !== operation) return;
      run.disabled = false;
      copy.disabled = !deps.canCopy;
      if (got.status !== 200 || !Array.isArray(got.body.rows)) {
        status.textContent = got.body?.message ?? "That report did not run.";
        return;
      }
      status.textContent = "";
      resultHost.append(deps.renderResult(got.body));
    }
    async function copyNow() {
      const request = ++operation;
      run.disabled = true;
      copy.disabled = true;
      status.textContent = "Saving.";
      const got = await copyReportTemplate(
        deps.baseUrl,
        deps.tenantId,
        template.id,
        template.publicationVersionId,
        copyKey,
      );
      if (!current() || request !== operation) return;
      run.disabled = false;
      copy.disabled = !deps.canCopy;
      if (got.status !== 201 || typeof got.body.id !== "string") {
        status.textContent = got.body?.message ?? "Could not save a copy.";
        return;
      }
      deps.openCopy(got.body.id);
    }
  }
  if (deps.autoOpen !== undefined) void detail(deps.autoOpen, true);
  else void list();
}

interface Publication {
  id: string;
  publicationVersionId: string;
  title: string;
  versionNumber: number;
}
interface Review {
  definitionId: string;
  sourceVersion: number;
  sourceHash: string;
  specHash: string;
  spec: Record<string, unknown>;
  members: Array<{ ref: string; name: string }>;
}
export function mountTemplatePublisher(
  parent: HTMLElement,
  deps: {
    definitionId: string;
    version: number;
    isCurrent: () => boolean;
    datasetLabel: (datasetId: string) => string;
    fieldLabel: (field: string, datasetId: string) => string;
    field?: (field: string, datasetId: string) => CustomReportFieldDto | undefined;
    dateBasis?: (datasetId: string) => string | undefined;
    request: <T>(
      method: "GET" | "POST",
      path: string,
      body?: unknown,
    ) => Promise<{ status: number; body: T & { message?: string } }>;
  },
): void {
  const widget = node("span");
  widget.className = "cr-template-publisher";
  parent.append(widget);
  const current = () => widget.isConnected && deps.isCurrent();
  const notice = node("span");
  notice.setAttribute("role", "status");
  let publication: Publication | null = null;
  let publicationOperation = 0;
  const publish = button("Publish as template", () => void review());
  publish.dataset["testid"] = "cr-template-publish";
  const withdraw = button("Withdraw", () => withdrawReview());
  withdraw.hidden = true;
  withdraw.dataset["testid"] = "cr-template-withdraw";
  widget.append(publish, withdraw, notice);
  function showPublication(value: Publication | null) {
    publication = value;
    withdraw.hidden = value === null;
    notice.textContent = value === null ? "" : `Published version ${value.versionNumber}`;
  }
  void deps
    .request<{ publication: Publication | null }>(
      "GET",
      `/franchisor/reports/custom/saved/${encodeURIComponent(deps.definitionId)}/template-publication`,
    )
    .then((got) => {
      if (current() && publicationOperation === 0 && got.status === 200)
        showPublication(got.body?.publication ?? null);
    });
  function dialog(title: string) {
    const sheet = node("dialog");
    sheet.className = "cr-template-dialog";
    sheet.append(node("h2", title));
    const error = node("p");
    error.className = "cr-error";
    error.setAttribute("role", "alert");
    const controls = node("div");
    controls.className = "cr-actions";
    const cancel = button("Cancel", () => {
      sheet.close();
      sheet.remove();
    });
    controls.append(cancel);
    sheet.append(error, controls);
    widget.append(sheet);
    sheet.showModal();
    sheet.addEventListener("close", () => sheet.remove(), { once: true });
    return { sheet, controls, error, cancel };
  }
  async function review() {
    publish.disabled = true;
    notice.textContent = "";
    const got = await deps.request<Review>(
      "POST",
      `/franchisor/reports/custom/saved/${encodeURIComponent(deps.definitionId)}/template-review`,
      { version: deps.version },
    );
    if (!current()) return;
    publish.disabled = false;
    if (got.status !== 200 || got.body?.spec === undefined) {
      notice.textContent = got.body?.message ?? "Could not review this template.";
      return;
    }
    const reviewed = got.body;
    const sheet = dialog("Publish as template");
    let posting = false;
    sheet.sheet.addEventListener("cancel", (event) => {
      if (posting) event.preventDefault();
    });
    sheet.sheet.insertBefore(node("h3", text(reviewed.spec["title"])), sheet.error);
    sheet.sheet.insertBefore(
      node(
        "p",
        `${
          reviewed.spec["specVersion"] === 2
            ? values(reviewed.spec["sources"])
                .map(record)
                .map((sourceItem) => deps.datasetLabel(text(sourceItem["dataset"])))
                .join(" + ")
            : deps.datasetLabel(text(reviewed.spec["dataset"]))
        }, report version ${reviewed.sourceVersion}`,
      ),
      sheet.error,
    );
    sheet.sheet.insertBefore(
      specSummary(
        reviewed.spec,
        (field) => deps.fieldLabel(field, text(reviewed.spec["dataset"])),
        "Each franchisee's own locations",
        deps,
      ),
      sheet.error,
    );
    const audience = node("fieldset");
    audience.append(node("legend", "Audience"));
    const all = node("input");
    all.type = "radio";
    all.name = "template-audience";
    all.checked = true;
    const pick = node("input");
    pick.type = "radio";
    pick.name = "template-audience";
    const allLabel = node("label");
    allLabel.append(all, " All current members");
    const pickLabel = node("label");
    pickLabel.append(pick, " Selected members");
    audience.append(allLabel, pickLabel);
    const choices = new Map<string, HTMLInputElement>();
    let requestId = crypto.randomUUID();
    for (const member of reviewed.members) {
      const choice = node("input");
      choice.type = "checkbox";
      choice.disabled = true;
      choices.set(member.ref, choice);
      const label = node("label");
      label.append(choice, ` ${member.name}`);
      audience.append(label);
      choice.addEventListener("change", sync);
    }
    sheet.sheet.insertBefore(audience, sheet.error);
    const confirm = button("Publish template", () => void commit(), true);
    confirm.dataset["testid"] = "cr-template-publish-confirm";
    sheet.controls.append(confirm);
    all.addEventListener("change", sync);
    pick.addEventListener("change", sync);
    function sync() {
      requestId = crypto.randomUUID();
      for (const choice of choices.values()) choice.disabled = all.checked;
      const count = all.checked
        ? reviewed.members.length
        : [...choices.values()].filter((choice) => choice.checked).length;
      confirm.textContent = `Publish to ${count} franchisees`;
      confirm.disabled = count === 0;
    }
    sync();
    async function commit() {
      if (posting) return;
      posting = true;
      publicationOperation += 1;
      confirm.disabled = true;
      sheet.cancel.disabled = true;
      all.disabled = true;
      pick.disabled = true;
      for (const choice of choices.values()) choice.disabled = true;
      const result = await deps.request<Publication>(
        "POST",
        "/franchisor/reports/custom/templates",
        {
          definitionId: reviewed.definitionId,
          sourceVersion: reviewed.sourceVersion,
          sourceHash: reviewed.sourceHash,
          specHash: reviewed.specHash,
          audience: all.checked ? "all_members" : "selected",
          memberRefs: all.checked
            ? []
            : [...choices].filter(([, choice]) => choice.checked).map(([ref]) => ref),
          requestId,
        },
      );
      if (!current() || !sheet.sheet.isConnected) return;
      if (result.status !== 201) {
        posting = false;
        sheet.cancel.disabled = false;
        all.disabled = false;
        pick.disabled = false;
        for (const choice of choices.values()) choice.disabled = all.checked;
        sheet.error.textContent = result.body?.message ?? "Could not publish this template.";
        confirm.disabled = false;
        return;
      }
      showPublication(result.body);
      sheet.sheet.close();
    }
  }
  function withdrawReview() {
    const currentPublication = publication;
    if (currentPublication === null) return;
    const selected = currentPublication;
    const sheet = dialog(`Withdraw ${selected.title}?`);
    let posting = false;
    sheet.sheet.addEventListener("cancel", (event) => {
      if (posting) event.preventDefault();
    });
    const confirm = button("Withdraw", () => void commit(), true);
    sheet.controls.append(confirm);
    async function commit() {
      if (posting) return;
      posting = true;
      publicationOperation += 1;
      confirm.disabled = true;
      sheet.cancel.disabled = true;
      const got = await deps.request<{ withdrawn: boolean }>(
        "POST",
        `/franchisor/reports/custom/templates/${encodeURIComponent(selected.id)}/withdraw`,
        { publicationVersionId: selected.publicationVersionId },
      );
      if (!current() || !sheet.sheet.isConnected) return;
      if (got.status !== 200) {
        posting = false;
        sheet.cancel.disabled = false;
        sheet.error.textContent = got.body?.message ?? "Could not withdraw this template.";
        confirm.disabled = false;
        return;
      }
      showPublication(null);
      sheet.sheet.close();
    }
  }
}
